/**
 * Botón "Exportar PDF" del header del expediente.
 *
 * Hace fetch del endpoint con auth header (no podemos usar `<a href>`
 * directo porque el endpoint requiere `Authorization: Bearer`),
 * genera un blob URL y dispara un click sintético para forzar el
 * download.
 *
 * **Gating de "estado vacío" (Sprint 11):** el PDF solo aporta valor
 * si hay algo que mostrar. Habilitamos el botón cuando el expediente
 * tiene CUALQUIERA de:
 * - status distinto a `draft` (ya avanzó),
 * - al menos 1 item del checklist completado,
 * - al menos 1 documento adjunto,
 * - BPIN registrado.
 *
 * Si nada de eso aplica, el botón queda disabled con un tooltip
 * explicando el porqué.
 */

import { useState } from "react";

import { ApiError, downloadProjectPdf, type ProjectDetailDTO } from "@/lib/api";
import { useActiveMunicipalityDane } from "@/lib/municipality";

import { exportDisabledReason, isExpedienteExportable } from "./_shared";

export function ExportPdfButton({ project }: { project: ProjectDetailDTO }) {
  const activeDane = useActiveMunicipalityDane();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const exportable = isExpedienteExportable(project);
  const disabledReason = exportDisabledReason(project);

  async function handleClick() {
    if (downloading || !exportable) return;
    setDownloading(true);
    setError(null);
    try {
      await downloadProjectPdf({
        projectId: project.id,
        dane: activeDane,
        fallbackName: `expediente_${project.id.slice(0, 8)}.pdf`,
      });
    } catch (exc) {
      const message = exc instanceof ApiError ? exc.message : "No se pudo exportar el PDF.";
      setError(message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={downloading || !exportable}
        title={disabledReason}
        aria-disabled={downloading || !exportable}
        data-testid="export-pdf-button"
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-xs transition hover:border-brand-400 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span aria-hidden="true">↓</span>
        {downloading ? "Generando…" : "Exportar PDF"}
      </button>
      {error && <span className="text-[11px] text-red-600">{error}</span>}
    </div>
  );
}
